import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Calculator, ArrowRight, Loader2, CheckCircle2 } from "lucide-react";
import PageHero from "../components/PageHero";
import Seo from "../components/Seo";
import { Reveal, Overline } from "../components/Reveal";
import { toast } from "sonner";
import api from "../lib/api";

const TIERS = [
  ["1 – 10", "0,85 €"], ["11 – 20", "0,80 €"], ["21 – 30", "0,75 €"], ["31 – 40", "0,70 €"],
  ["41 – 50", "0,65 €"], ["51 – 70", "0,60 €"], ["71 – 100", "0,55 €"], ["101 – 110", "0,50 €"],
];

const fmt = (n) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(n);

export default function Tarifs() {
  const [logements, setLogements] = useState(4);
  const [surface, setSurface] = useState(40);
  const [result, setResult] = useState(null);
  const [gridTiers, setGridTiers] = useState(TIERS);
  const [tiersNote, setTiersNote] = useState("");

  useEffect(() => {
    api.getTiers().then((r) => {
      if (r.tiers?.length) {
        setGridTiers(r.tiers.map((t) => [`${t.min} – ${t.max}`, `${String(t.price).replace(".", ",")} €`]));
        setTiersNote(r.note || "");
      }
    }).catch(() => {});
  }, []);
  const [lead, setLead] = useState({ nom: "", email: "", telephone: "", message: "", consentement: false });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    let ignore = false;
    const run = async () => {
      try {
        const r = await api.estimate({ logements: Number(logements) || 1, surface_moyenne: Number(surface) || 1 });
        if (!ignore) setResult(r);
      } catch (e) { /* ignore transient */ }
    };
    const id = setTimeout(run, 250);
    return () => { ignore = true; clearTimeout(id); };
  }, [logements, surface]);

  const submitLead = async (e) => {
    e.preventDefault();
    const hp = e.target.hp?.value || "";
    if (!lead.consentement) return toast.error("Merci d'accepter le traitement de vos données.");
    setSubmitting(true);
    try {
      const r = await api.estimateLead({
        ...lead,
        logements: Number(logements),
        surface_moyenne: Number(surface),
        hp,
      });
      toast.success("Estimation transmise !", { description: `Référence de dossier : ${r.reference}` });
      setDone(r.reference);
      setLead({ nom: "", email: "", telephone: "", message: "", consentement: false });
    } catch (err) {
      toast.error("Une erreur est survenue. Réessayez.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="tarifs-page">
      <Seo title="Abonnements & tarifs" path="/tarifs" description="Tarification au m² dégressive et calculateur d'abonnement Speed Détresse. Un logement = un abonnement." />
      <PageHero
        overline="Tarifs indicatifs de lancement"
        title={["Estimez votre", "abonnement"]}
        subtitle="Un logement = un abonnement. Le prix au m² baisse à mesure que votre parc grandit. Tarifs indicatifs de lancement, sous réserve de validation définitive à l'ouverture de l'application."
        testid="tarifs-hero"
      />

      <section className="relative z-10 mx-auto max-w-7xl px-5 py-16 lg:px-8">
        <div className="grid gap-10 lg:grid-cols-[1.1fr_0.9fr]">
          {/* Calculator */}
          <Reveal>
            <div className="rounded-3xl border border-white/10 bg-navy-surface/60 p-8 md:p-10" data-testid="calculator-card">
              <div className="flex items-center gap-3 text-gold"><Calculator className="h-6 w-6" strokeWidth={1.5} /><Overline>Calculateur</Overline></div>

              <div className="mt-8 space-y-8">
                <div>
                  <div className="flex items-baseline justify-between">
                    <label className="text-sm text-slate-300">Nombre de logements</label>
                    <span data-testid="calc-logements-value" className="font-serif text-3xl text-white">{logements}</span>
                  </div>
                  <input
                    data-testid="calc-logements-slider"
                    type="range" min="1" max="150" value={logements}
                    onChange={(e) => setLogements(e.target.value)}
                    className="mt-3 w-full accent-gold"
                  />
                </div>
                <div>
                  <div className="flex items-baseline justify-between">
                    <label className="text-sm text-slate-300">Surface moyenne / logement (m²)</label>
                    <span data-testid="calc-surface-value" className="font-serif text-3xl text-white">{surface} m²</span>
                  </div>
                  <input
                    data-testid="calc-surface-slider"
                    type="range" min="10" max="300" value={surface}
                    onChange={(e) => setSurface(e.target.value)}
                    className="mt-3 w-full accent-gold"
                  />
                </div>
              </div>

              {result && (
                <motion.div
                  key={`${result.sur_devis}-${result.prix_mensuel}`}
                  initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                  className="mt-8 rounded-2xl border border-gold/25 bg-navy p-7"
                  data-testid="calc-result"
                >
                  {result.sur_devis ? (
                    <div>
                      <p className="text-sm uppercase tracking-wide text-gold">Grand parc</p>
                      <p className="mt-2 font-serif text-4xl text-white">Sur devis</p>
                      <p className="mt-2 text-sm text-slate-400">Au-delà de 110 logements : tarification négociée sur mesure.</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <p className="text-xs uppercase tracking-wide text-slate-400">Prix au m²</p>
                        <p className="mt-1 font-serif text-3xl text-gold">{fmt(result.prix_m2)}</p>
                      </div>
                      <div>
                        <p className="text-xs uppercase tracking-wide text-slate-400">Surface totale</p>
                        <p className="mt-1 font-serif text-3xl text-white">{result.surface_totale} m²</p>
                      </div>
                      <div>
                        <p className="text-xs uppercase tracking-wide text-slate-400">Estimation mensuelle</p>
                        <p data-testid="calc-monthly" className="mt-1 font-serif text-4xl text-white">{fmt(result.prix_mensuel)}</p>
                      </div>
                      <div>
                        <p className="text-xs uppercase tracking-wide text-slate-400">Estimation annuelle</p>
                        <p data-testid="calc-annual" className="mt-1 font-serif text-4xl text-white">{fmt(result.prix_annuel)}</p>
                      </div>
                    </div>
                  )}
                  <p className="mt-5 text-xs text-slate-500">Estimation indicative. Le tarif définitif est confirmé lors du rendez-vous.</p>
                </motion.div>
              )}
            </div>
          </Reveal>

          {/* Lead form */}
          <Reveal delay={0.12}>
            {done ? (
              <div data-testid="form-success" className="rounded-3xl border border-gold/25 bg-navy-elevated p-8 text-center md:p-10">
                <CheckCircle2 className="mx-auto h-14 w-14 text-gold" strokeWidth={1.3} />
                <h3 className="mt-5 font-serif text-3xl text-white">Estimation transmise</h3>
                <p className="mt-3 text-slate-300">Vos coordonnées et le calcul ont été enregistrés. Nous vous recontactons pour organiser un rendez-vous.</p>
                <p className="mt-4 inline-block rounded-full border border-gold/40 px-4 py-2 font-mono text-sm text-gold" data-testid="success-reference">Réf. {done}</p>
                <button onClick={() => setDone(null)} className="mt-8 block w-full rounded-full border border-white/15 px-6 py-3 text-sm text-white hover:border-gold/50">
                  Nouvelle estimation
                </button>
              </div>
            ) : (
            <form onSubmit={submitLead} data-testid="estimate-lead-form" className="rounded-3xl border border-white/10 bg-navy-surface/60 p-8 md:p-10">
              <input type="text" name="hp" defaultValue="" tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" />
              <h3 className="font-serif text-2xl text-white">Recevoir mon estimation</h3>
              <p className="mt-2 text-sm text-slate-400">Vos coordonnées et le calcul sont transmis pour organiser un rendez-vous.</p>
              <div className="mt-6 space-y-4">
                <input required data-testid="lead-nom" placeholder="Nom / société" value={lead.nom}
                  onChange={(e) => setLead({ ...lead, nom: e.target.value })} className={inputCls} />
                <input required type="email" data-testid="lead-email" placeholder="E-mail" value={lead.email}
                  onChange={(e) => setLead({ ...lead, email: e.target.value })} className={inputCls} />
                <input required data-testid="lead-telephone" placeholder="Téléphone" value={lead.telephone}
                  onChange={(e) => setLead({ ...lead, telephone: e.target.value })} className={inputCls} />
                <textarea rows="3" data-testid="lead-message" placeholder="Message (optionnel)" value={lead.message}
                  onChange={(e) => setLead({ ...lead, message: e.target.value })} className={inputCls} />
                <label className="flex items-start gap-3 text-xs text-slate-400">
                  <input type="checkbox" data-testid="lead-consent" checked={lead.consentement}
                    onChange={(e) => setLead({ ...lead, consentement: e.target.checked })} className="mt-0.5 accent-gold" />
                  J'accepte que mes données soient traitées pour ma demande.
                </label>
              </div>
              <button type="submit" disabled={submitting} data-testid="lead-submit"
                className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-gold px-6 py-3.5 text-sm font-semibold text-navy transition-colors hover:bg-gold-light disabled:opacity-60">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Envoyer mon estimation <ArrowRight className="h-4 w-4" /></>}
              </button>
            </form>
            )}
          </Reveal>
        </div>

        {/* Tiers table */}
        <Reveal className="mt-20">
          <Overline>Grille dégressive</Overline>
          <div className="mt-6 grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-white/10 bg-white/10 sm:grid-cols-4">
            {gridTiers.map(([range, price]) => (
              <div key={range} className="bg-navy p-6">
                <p className="text-xs text-slate-400">{range} logements</p>
                <p className="mt-2 font-serif text-3xl text-gold">{price}</p>
                <p className="text-xs text-slate-500">/ m² / mois</p>
              </div>
            ))}
          </div>
          <p className="mt-4 rounded-xl border border-gold/25 bg-gold/5 p-4 text-sm text-gold" data-testid="launch-notice">
            Tarifs indicatifs de lancement, sous réserve de validation définitive à l'ouverture de l'application.
          </p>
          {tiersNote && <p className="mt-3 text-sm text-slate-400" data-testid="tiers-note">{tiersNote}</p>}
          <p className="mt-3 text-sm text-slate-500">
            Règle : une maison divisée en 4 appartements = 4 logements. Une colocation reste 1 logement, facturé selon la surface totale.
          </p>
        </Reveal>

        {/* Prestation unique vs abonnement */}
        <Reveal className="mt-20" data-testid="tarifs-compare">
          <Overline>Deux façons de profiter de Speed Détresse</Overline>
          <div className="mt-8 grid gap-6 md:grid-cols-2">
            <div className="rounded-3xl border border-white/10 bg-navy-surface/60 p-8" data-testid="tarifs-unique-card">
              <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Sans engagement</p>
              <h3 className="mt-3 font-serif text-3xl text-white">La prestation unique</h3>
              <p className="mt-4 text-sm leading-relaxed text-slate-300">
                Un dépannage ponctuel, 7 jours sur 7, sans abonnement : prix connu avant de confirmer,
                créneau au choix, et chaque intervention conservée dans votre livret de dépannages.
              </p>
              <Link to="/depannage" data-testid="tarifs-unique-link" className="mt-6 inline-flex items-center gap-2 text-sm text-gold hover:underline">
                Découvrir les prestations uniques <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
            <div className="overflow-hidden rounded-3xl border border-gold/30 bg-navy-elevated" data-testid="tarifs-abo-card">
              <img src="/images/story-senior.webp" alt="Prestation offerte pour les aînés de la famille" loading="lazy" className="aspect-[16/7] w-full object-cover" />
              <div className="p-8">
              <p className="text-xs uppercase tracking-[0.2em] text-gold">Speed Détresse — l'écosystème complet du logement</p>
              <h3 className="mt-3 font-serif text-3xl text-white">Un abonnement qui transforme vos mètres carrés en sérénité</h3>
              <p className="mt-4 text-sm leading-relaxed text-slate-300">
                Dépannage visible inclus, QR code permanent, états des lieux numériques, Carnet de Santé, devis et partenaires,
                marketplace entre propriétaires abonnés… et chaque année,
                <span className="text-gold"> 2 prestations offertes pour les personnes âgées de votre famille</span>.
              </p>
              <p className="mt-6 text-sm text-slate-400">Estimez votre tarif avec le calculateur ci-dessus.</p>
              </div>
            </div>
          </div>
        </Reveal>

        {/* PRÉVENTION & PRIME */}
        <Reveal className="mt-16" data-testid="tarifs-prevention">
          <div className="rounded-3xl border border-white/10 bg-navy-surface/60 p-8 md:p-10">
            <Overline>Prévention & entretien</Overline>
            <h3 className="mt-4 max-w-2xl font-serif text-3xl tracking-tight text-white">Entretenir aujourd'hui pour éviter de réparer demain.</h3>
            <p className="mt-4 max-w-3xl text-sm leading-relaxed text-slate-300">
              Un logement bien entretenu se dégrade moins : le suivi régulier permet de mieux connaître le logement,
              ses équipements, ses interventions passées et les opérations à prévoir.
            </p>
            <p className="mt-4 max-w-3xl text-sm leading-relaxed text-slate-400">
              Speed Détresse pourra intégrer un mécanisme de valorisation des logements régulièrement entretenus :
              selon les critères définis et les conditions de l'offre, une prime de fidélité ou de bon entretien
              pouvant aller jusqu'à 2 % par an pourrait être envisagée. Cette prime n'est pas garantie et reste
              soumise à la validation définitive des conditions commerciales.
            </p>
          </div>
        </Reveal>
      </section>
    </div>
  );
}

const inputCls =
  "w-full rounded-xl border border-white/10 bg-navy px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:border-gold focus:outline-none focus:ring-1 focus:ring-gold";
